/**
 * 튜닝 챌린지 「평가 기준」 창 — 참여자가 문제 안의 버튼으로 연다(사용자 요청 2026-10-05).
 *
 * 숫자는 **서버가 채점에 쓰는 상수**(`/challenge/tuning-rubric`)를 받아 문항의 덮어쓰기를 얹어 보여 준다.
 * 화면에 기준 블록 수를 따로 적어 두면 채점이 바뀔 때 창의 글만 남아 거짓말이 된다.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAward } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { fetchTuningRubric } from '../../services/tuningGrade';

const KINDS = ['sql', 'new', 'modify'];

/** 서버 기본값에 문항의 덮어쓰기를 얹는다(숫자만) */
function mergeThresholds(base, over) {
  const out = {};
  for (const k of KINDS) {
    const b = (base && base[k]) || {};
    const o = (over && over[k]) || {};
    out[k] = {
      full: Number.isFinite(Number(o.full)) && o.full != null ? Number(o.full) : b.full,
      partial: Number.isFinite(Number(o.partial)) && o.partial != null ? Number(o.partial) : b.partial,
    };
  }
  return out;
}

const cell = { padding: '4px 8px', borderBottom: '1px solid var(--color-border)', textAlign: 'left' };

export default function TuningRubricDialog({ open, onClose, scope, thresholds }) {
  const { t } = useTranslation();
  const [rubric, setRubric] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || rubric) return undefined;
    let alive = true;
    fetchTuningRubric()
      .then((r) => { if (alive) setRubric(r); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [open, rubric]);

  const th = rubric ? mergeThresholds(rubric.thresholds, thresholds) : null;
  const overridden = !!(thresholds && KINDS.some(k => thresholds[k]));
  const kinds = scope === 'sql+index' ? KINDS : ['sql'];

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t('challenge:rubric.title')}
      icon={<FiAward size={14} />}
      closeOnBackdrop
    >
      <div style={{ padding: 12, fontSize: 12, lineHeight: 1.6, color: 'var(--color-text)' }}>
        <p style={{ margin: '0 0 8px' }}>{t('challenge:rubric.intro')}</p>
        <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
          {scope === 'sql+index' ? t('challenge:rubric.scopeIndex') : t('challenge:rubric.scopeSql')}
        </p>

        <ol style={{ margin: '0 0 8px', paddingLeft: 18 }}>
          <li>{t('challenge:rubric.ruleRows')}</li>
          <li>{t('challenge:rubric.ruleBaseline')}</li>
          <li>{t('challenge:rubric.ruleModel')}</li>
          <li>{t('challenge:rubric.ruleTable')}</li>
          {scope === 'sql+index' && (
            <li>
              {t('challenge:rubric.rulePenalty', {
                worse: rubric ? rubric.penaltyWorse : '—',
                marginal: rubric ? rubric.penaltyMarginal : '—',
                gain: rubric ? Math.round(rubric.minIndexGain * 100) : '—',
              })}
            </li>
          )}
        </ol>

        {failed && <p style={{ color: 'var(--color-quiz-fail-text)' }}>{t('challenge:rubric.loadFailed')}</p>}
        {th && (
          <table style={{ width: '100%', borderCollapse: 'collapse', margin: '4px 0 8px', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-secondary)' }}>
                <th style={cell}>{t('challenge:rubric.colKind')}</th>
                <th style={cell}>{t('challenge:rubric.colFull')}</th>
                <th style={cell}>{t('challenge:rubric.colPartial')}</th>
              </tr>
            </thead>
            <tbody>
              {kinds.map(k => (
                <tr key={k}>
                  <td style={cell}>{t(`challenge:tuning.rubric.kind.${k}`)}</td>
                  <td style={cell}>{t('challenge:rubric.within', { n: Number(th[k].full).toLocaleString() })}</td>
                  <td style={cell}>{t('challenge:rubric.within', { n: Number(th[k].partial).toLocaleString() })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {overridden && <p style={{ margin: '0 0 8px', fontSize: 11 }}>{t('challenge:rubric.overridden')}</p>}

        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
          <div>· {t('challenge:rubric.noteBlocks')}</div>
          {scope === 'sql+index' && (
            <>
              <div>· {t('challenge:rubric.notePhases')}</div>
              <div>· {t('challenge:rubric.noteModify')}</div>
              <div>· {t('challenge:rubric.noteMax', { n: rubric ? rubric.maxIndexes : '—' })}</div>
            </>
          )}
          <div>· {t('challenge:rubric.noteOnline')}</div>
        </div>
      </div>
    </AppModal>
  );
}
